import { useState, useCallback, useRef, useEffect } from 'react';
import { ScanSearch } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/store/authStore';
import { useScreenSources } from '@/features/screening/hooks/useScreenSources';
import { useCreateReview, useAddSourcesToReview, useReviewSources } from '@/features/review/hooks/useReview';
import { useCreateTeam, useTeams } from '@/features/team/hooks/useTeam';
import { ScreeningSetup } from '@/features/screening/components/ScreeningSetup';
import { ScreeningProgress } from '@/features/screening/components/ScreeningProgress';
import { ScreeningDashboard } from '@/features/screening/components/ScreeningDashboard';
import { ScreeningQueue } from '@/features/screening/components/ScreeningQueue';
import type { ScreeningProgress as ScreeningProgressType } from '@/features/evaluation/types';
import type { Review } from '@/features/review/types';

type Phase = 'setup' | 'ai_scoring' | 'screening';

export function Screening() {
  const user = useAuthStore((s) => s.user);
  const [phase, setPhase] = useState<Phase>('setup');
  const [reviewId, setReviewId] = useState<string | null>(null);
  const [review, setReview] = useState<Review | null>(null);
  const [progress, setProgress] = useState<ScreeningProgressType>({
    total_sources: 0,
    total_batches: 0,
    completed_batches: 0,
    completed_sources: 0,
    failed_batches: 0,
    results: [],
  });
  const [totalSources, setTotalSources] = useState(0);
  const [consecutiveExcludes, setConsecutiveExcludes] = useState(0);
  const cancelRef = useRef(false);

  const screenSources = useScreenSources();
  const createReview = useCreateReview();
  const addSources = useAddSourcesToReview();
  const createTeam = useCreateTeam();
  const { data: teams = [] } = useTeams();
  const { data: reviewSources } = useReviewSources(reviewId ?? undefined, 'screening');

  // Track consecutive excludes from review decisions
  useEffect(() => {
    if (!reviewSources) return;
    // This is recalculated based on the ScreeningQueue's decision history
    // We track it via a simpler approach: count from the queue
  }, [reviewSources]);

  const getOrCreatePersonalTeam = useCallback(async (): Promise<string> => {
    // Check for existing team owned by user
    const personalTeam = teams.find((t) => t.owner_id === user?.id);
    if (personalTeam) return personalTeam.id;

    // Create a personal team
    const team = await createTeam.mutateAsync('My Reviews');
    return team.id;
  }, [teams, user, createTeam]);

  const handleStart = useCallback(
    async (sourceIds: string[], criteriaIds: string[]) => {
      if (!user) return;
      cancelRef.current = false;

      try {
        // 1. Get or create personal team
        const teamId = await getOrCreatePersonalTeam();

        // 2. Create review
        const newReview = await createReview.mutateAsync({
          team_id: teamId,
          name: `Screening ${new Date().toLocaleDateString()}`,
          review_mode: 'ai_human',
          settings: { screening_order: 'priority' },
        });

        setReview(newReview);
        setReviewId(newReview.id);
        setTotalSources(sourceIds.length);

        // 3. Add sources to review
        await addSources.mutateAsync({
          reviewId: newReview.id,
          sourceIds: sourceIds,
        });

        // 4. Transition to AI scoring phase
        setPhase('ai_scoring');

        // 5. Run parallel screening with reviewId bridge
        await screenSources.mutateAsync({
          source_ids: sourceIds,
          criteria_ids: criteriaIds,
          reviewId: newReview.id,
          onProgress: (p) => {
            if (cancelRef.current) return;
            setProgress(p);
          },
        });
      } catch {
        if (!cancelRef.current) {
          toast.error('Failed to start screening');
          setPhase('setup');
        }
      }
    },
    [user, getOrCreatePersonalTeam, createReview, addSources, screenSources],
  );

  const handleCancel = useCallback(() => {
    cancelRef.current = true;
    // Transition to screening phase with whatever results we have
    setPhase('screening');
  }, []);

  const handleAIComplete = useCallback(() => {
    setPhase('screening');
  }, []);

  const handleFinish = useCallback(async () => {
    if (reviewId) {
      await supabase
        .from('reviews')
        .update({ status: 'completed' })
        .eq('id', reviewId);
    }
    toast.success('Screening finished');
    setPhase('setup');
    setReviewId(null);
    setReview(null);
  }, [reviewId]);

  const isStarting = createReview.isPending || addSources.isPending || createTeam.isPending;

  return (
    <div className="max-w-7xl mx-auto space-y-6 p-6">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
          <ScanSearch className="h-6 w-6 text-indigo-600" />
          Screening
        </h1>
        <p className="text-sm text-slate-500">
          AI-assisted screening workflow — score, review, decide
        </p>
      </div>

      {/* Phase: Setup */}
      {phase === 'setup' && (
        <ScreeningSetup onStart={handleStart} isStarting={isStarting} />
      )}

      {/* Phase: AI Scoring */}
      {phase === 'ai_scoring' && (
        <ScreeningProgress
          progress={progress}
          onCancel={handleCancel}
          onComplete={handleAIComplete}
        />
      )}

      {/* Phase: Human Screening */}
      {phase === 'screening' && review && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <ScreeningQueue
              review={review}
              phase="screening"
              onConsecutiveExcludesChange={setConsecutiveExcludes}
            />
          </div>
          <div className="lg:col-span-1">
            <ScreeningDashboard
              reviewId={review.id}
              totalSources={totalSources}
              consecutiveExcludes={consecutiveExcludes}
              onFinish={handleFinish}
            />
          </div>
        </div>
      )}
    </div>
  );
}
